import { useCallback, useMemo, useRef, useState } from 'react';

import { toast } from '@commenda/kakashi';
import { useReport } from '../context/ReportContext';
import {
  COMPARISON_MENU_IDS,
  COMPARISON_MENU_LABELS,
  InlineOverrideHelper,
  PERIOD_PRESET_IDS,
  PERIOD_PRESET_LABELS,
} from '../helpers/InlineOverrideHelper';
import { PeriodHelper } from '../helpers/PeriodHelper';
import type { MonthKey, PeriodOverride } from '../types/period';
import type { IReportItem } from '../types/report';

export interface IOverrideMenuOption {
  label: string;
  checked: boolean;
  onSelect: () => void;
}

export interface IOverrideMenuGroup {
  options: IOverrideMenuOption[];
}

export interface IInlineOverrideState {
  periodGroups: IOverrideMenuGroup[];
  compareOptions: IOverrideMenuOption[];
  customRange: {
    open: boolean;
    setOpen: (open: boolean) => void;
    initialFrom: MonthKey;
    initialTo: MonthKey;
    months: MonthKey[];
    booksClosedThrough: MonthKey;
    apply: (range: { from: MonthKey; to: MonthKey }) => void;
  };
  clearOverride: () => void;
  /** Wire to the card menu's onOpenChange; the range popover opens once the menu has closed. */
  onMenuOpenChange: (open: boolean) => void;
}

/** Card-menu period override: every change applies immediately and can be undone from a toast. */
export const useInlineOverride = (
  item: IReportItem,
  title: string
): IInlineOverrideState => {
  const { dataset, table, filter, booksClosedThrough, setItems } = useReport();
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const pendingCustomRange = useRef(false);
  const override = item.override ?? null;

  const onMenuOpenChange = useCallback((open: boolean) => {
    if (open || !pendingCustomRange.current) return;
    pendingCustomRange.current = false;
    setIsCustomRangeOpen(true);
  }, []);

  const reportPeriod = useMemo(
    () =>
      PeriodHelper.resolve(
        filter,
        booksClosedThrough,
        table.fiscalYearStartMonth
      ),
    [filter, booksClosedThrough, table.fiscalYearStartMonth]
  );

  const setOverride = useCallback(
    (next: PeriodOverride | null) => {
      setItems((items) =>
        items.map((i) =>
          i.instanceId === item.instanceId ? { ...i, override: next } : i
        )
      );
    },
    [item.instanceId, setItems]
  );

  const apply = useCallback(
    (next: PeriodOverride | null) => {
      const previous = override ? { ...override } : null;
      setOverride(next);
      toast(`Period changed for ${title}`, {
        action: { label: 'Undo', onClick: () => setOverride(previous) },
      });
    },
    [override, setOverride, title]
  );

  const activePreset = InlineOverrideHelper.activePreset(override, filter);
  const hasPeriodOverride = InlineOverrideHelper.hasPeriodOverride(override);

  const periodGroups: IOverrideMenuGroup[] = [
    {
      options: [
        {
          label: `Report period (${reportPeriod.label})`,
          checked: !hasPeriodOverride,
          onSelect: () => apply(InlineOverrideHelper.clearPeriod(override)),
        },
      ],
    },
    {
      options: PERIOD_PRESET_IDS.map((preset) => ({
        label: PERIOD_PRESET_LABELS[preset],
        checked: activePreset === preset,
        onSelect: () =>
          apply(InlineOverrideHelper.withPreset(override, preset, filter)),
      })),
    },
    {
      options: [
        {
          label: 'Custom range…',
          checked: InlineOverrideHelper.isCustomRange(override),
          onSelect: () => {
            pendingCustomRange.current = true;
          },
        },
      ],
    },
  ];

  const compareOptions: IOverrideMenuOption[] = [
    {
      label: `Report setting (${COMPARISON_MENU_LABELS[filter.comparison]})`,
      checked: override?.comparison === undefined,
      onSelect: () => apply(InlineOverrideHelper.clearComparison(override)),
    },
    ...COMPARISON_MENU_IDS.map((comparison) => ({
      label: COMPARISON_MENU_LABELS[comparison],
      checked: override?.comparison === comparison,
      onSelect: () =>
        apply(InlineOverrideHelper.withComparison(override, comparison)),
    })),
  ];

  const isCustomRange = InlineOverrideHelper.isCustomRange(override);
  const initialTo =
    isCustomRange && override?.anchor ? override.anchor : filter.anchor;
  const initialFrom =
    isCustomRange && override?.rangeStart ? override.rangeStart : initialTo;

  return {
    periodGroups,
    compareOptions,
    customRange: {
      open: isCustomRangeOpen,
      setOpen: setIsCustomRangeOpen,
      initialFrom,
      initialTo,
      months: dataset.months.map((m) => m.month),
      booksClosedThrough,
      apply: ({ from, to }) => {
        setIsCustomRangeOpen(false);
        apply(InlineOverrideHelper.withRange(override, from, to));
      },
    },
    clearOverride: () => apply(null),
    onMenuOpenChange,
  };
};
